import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import {
  APIClientRecord,
  APIClientRoleOption,
} from '../../../hooks/api-client-hooks';

import {
  APIClientFormState,
  clientOwnerLabel,
  dateLabel,
  DialogMode,
  emptyApiClientForm,
  formFromClient,
  normalizeSelectStringArray,
  normalizeText,
} from './apiClientUtils';

type Props = {
  open: boolean;
  mode: DialogMode;
  initialValue: APIClientRecord | null;
  roleOptions: APIClientRoleOption[];
  saving?: boolean;
  onClose: () => void;
  onSubmit: (form: APIClientFormState) => Promise<void>;
};

const APIClientEditorDialog: React.FC<Props> = ({
                                                  open,
                                                  mode,
                                                  initialValue,
                                                  roleOptions,
                                                  saving,
                                                  onClose,
                                                  onSubmit,
                                                }) => {
  const [form, setForm] = React.useState<APIClientFormState>(emptyApiClientForm);
  const [error, setError] = React.useState<string | null>(null);

  const readOnly = mode === 'view';
  const isCreate = mode === 'create';

  React.useEffect(() => {
    if (!open) return;

    setForm(isCreate ? emptyApiClientForm : formFromClient(initialValue));
    setError(null);
  }, [open, isCreate, initialValue]);

  const setField = (key: keyof APIClientFormState, value: any) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const submit = async () => {
    setError(null);

    if (!normalizeText(form.name)) {
      setError('Name is required.');
      return;
    }

    if (!Number.isFinite(Number(form.token_ttl_seconds)) || Number(form.token_ttl_seconds) <= 0) {
      setError('Token TTL must be a positive number.');
      return;
    }

    try {
      await onSubmit(form);
    } catch (err: any) {
      setError(err?.message || 'Failed to save API client.');
    }
  };

  const title =
    mode === 'create'
      ? 'Create API Client'
      : mode === 'edit'
        ? `Edit API Client: ${initialValue?.name || initialValue?.client_id || ''}`
        : `View API Client: ${initialValue?.name || initialValue?.client_id || ''}`;

  const selectedRoleIds = form.role_ids || [];

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          {!isCreate && initialValue?.client_id ? (
            <TextField
              fullWidth
              label="Client ID"
              value={initialValue.client_id}
              InputProps={{
                readOnly: true,
              }}
            />
          ) : null}

          {!isCreate && initialValue ? (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Owner
              </Typography>

              <Typography variant="body2">
                {clientOwnerLabel(initialValue)}
              </Typography>
            </Box>
          ) : null}

          <TextField
            fullWidth
            required
            label="Name"
            value={form.name}
            onChange={(event) => setField('name', event.target.value)}
            disabled={readOnly || saving}
          />

          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Description"
            value={form.description}
            onChange={(event) => setField('description', event.target.value)}
            disabled={readOnly || saving}
          />

          {!isCreate ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Switch
                checked={!!form.enabled}
                onChange={(event) => setField('enabled', event.target.checked)}
                disabled={readOnly || saving}
              />

              <Typography variant="body2">
                Enabled
              </Typography>
            </Stack>
          ) : null}

          <TextField
            fullWidth
            type="number"
            label="Token TTL Seconds"
            value={form.token_ttl_seconds}
            onChange={(event) => setField('token_ttl_seconds', Number(event.target.value))}
            disabled={readOnly || saving}
            helperText="Lifetime of issued API client access tokens."
          />

          <FormControl fullWidth disabled={readOnly || saving}>
            <InputLabel id="api-client-role-ids-label">Roles</InputLabel>

            <Select
              labelId="api-client-role-ids-label"
              multiple
              value={selectedRoleIds.map(String)}
              input={<OutlinedInput label="Roles" />}
              onChange={(event) => {
                const values = normalizeSelectStringArray(event.target.value);

                setField(
                  'role_ids',
                  values
                    .map((value) => Number(value))
                    .filter((value) => Number.isFinite(value)),
                );
              }}
              renderValue={(selected) => {
                const selectedIds = selected as string[];

                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedIds.map((id) => {
                      const role = roleOptions.find((option) => String(option.id) === String(id));

                      return (
                        <Chip
                          key={id}
                          size="small"
                          label={role?.name || id}
                        />
                      );
                    })}
                  </Box>
                );
              }}
            >
              {roleOptions.map((role) => {
                const checked = selectedRoleIds.map(String).includes(String(role.id));

                return (
                  <MenuItem key={role.id} value={String(role.id)}>
                    <Checkbox checked={checked} />
                    <ListItemText
                      primary={role.name}
                      secondary={role.description || undefined}
                    />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {!isCreate && initialValue ? (
            <>
              <Divider />

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Created: ${dateLabel(initialValue.created)}`}
                />

                <Chip
                  size="small"
                  variant="outlined"
                  label={`Updated: ${dateLabel(initialValue.last_updated)}`}
                />

                <Chip
                  size="small"
                  variant="outlined"
                  label={`Last Used: ${dateLabel(initialValue.last_used_at)}`}
                />
              </Stack>
            </>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {readOnly ? 'Close' : 'Cancel'}
        </Button>

        {!readOnly ? (
          <Button variant="contained" onClick={submit} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export default APIClientEditorDialog;